import { getApiTokensClient, getRailsClient, getRailsFormClient } from "sj-ui-toolkit/src/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RailsLicenseUser {
  directoryUuid: string;
  licensed: boolean;
  email?: string;
  archived?: boolean;
}

export interface AdminUser {
  directoryUuid: string;
  email?: string;
  notificationSetting?: boolean;
}

export interface RailsUser {
  id?: string;
  directoryUuid?: string;
  email?: string;
  licensed?: boolean;
  archived?: boolean;
  receiveActivityNotifications?: boolean;
}

export interface Lesson {
  name?: string;
  completedAt?: string | null;
  result?: string | null;
  passed?: boolean | null;
  hackedu?: boolean;
}

export interface Belt {
  id?: string;
  name?: string;
  levelName?: string;
  completedAt?: string;
  timeSpent?: string;
  lessonsCompleteCount?: number;
  roleId?: string;
  roleEnrollmentPublicId?: string;
  emailUrl?: string;
}

export interface CsvImportResponse {
  success?: boolean;
  error?: unknown;
  data?: {
    rejection_details?: Record<string, unknown>;
  };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export const loadLicensesAsync = async (): Promise<RailsLicenseUser[]> => {
  const API = "/api/internal_api/v1/admin/users/licenses";
  const { data } = await getRailsClient().get<RailsLicenseUser[]>(API);
  return data;
};

export const loadAdminsAsync = async (): Promise<{ admins: AdminUser[] }> => {
  const API = "/api/internal_api/v1/admin/admins";
  const { data } = await getRailsClient().get<{ admins: AdminUser[] }>(API);
  return data;
};

export const loadUserByRailsId = async (
  id: string
): Promise<{ user: RailsUser; lessons: Lesson[]; passedBelts: Belt[] }> => {
  const API = `/api/internal_api/v1/admin/users/${id}`;
  const { data } = await getRailsClient().get<{
    user: RailsUser;
    lessons: Lesson[];
    passedBelts: Belt[];
  }>(API);
  if (!data.user) throw new Error(`User not found for id: ${id}`);
  return data;
};

export const unlockUserAsync = async (id: string): Promise<void> => {
  const API = `/api/internal_api/v1/admin/users/${id}/unlock`;
  await getRailsClient().put(API);
};

export const checkIfUserLockedAsync = async (uuid: string): Promise<{ isUnlocked: boolean }> => {
  const API = `/api/internal_api/v1/admin/users/${uuid}/check_locked_status`;
  const { data } = await getRailsClient().get<{ isUnlocked: boolean }>(API);
  return data;
};

export const addLicenseAsync = async (userId: string): Promise<unknown> => {
  const API = `/api/internal_api/v1/admin/users/${userId}/add_license`;
  const { data } = await getRailsClient().put(API);
  return data;
};

// Success/failure is signaled by HTTP status: render_response returns an error status on
// failure (which the rails client throws on), so callers discriminate via try/catch, not a
// body flag. The response body isn't consumed.
export type RequestSeatsResponse = Record<string, unknown>;

// Requests additional user license seats for the current tenant. The BE derives the tenant
// and requesting admin from the authenticated session and emails the sales alias; the FE only
// sends the count as `requestedSeats` (auto-decamelized to `requested_seats` by the rails client).
export const requestSeatsAsync = async (requestedSeats: number): Promise<RequestSeatsResponse> => {
  const API = "/api/internal_api/atlas/v1/admin/users/purchase_seats";
  const { data } = await getRailsClient().post<RequestSeatsResponse>(API, { requestedSeats });
  return data;
};

export const updateUserNotificationsAsync = async (
  id: string,
  receiveActivityNotifications: boolean
): Promise<void> => {
  const API = `/api/internal_api/v1/admin/users/${id}`;
  await getRailsClient().put(API, { user: { receiveActivityNotifications } });
};

export const importUsersFromCSVAsync = async (file: File): Promise<CsvImportResponse> => {
  const API = "/api/internal_api/v2/upload-users";
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await getRailsFormClient().post<CsvImportResponse["data"]>(API, formData);
    return { success: true, data: response.data };
  } catch (e) {
    return { success: false, error: e };
  }
};

export const getScimTokenAsync = async (): Promise<boolean> => {
  const { data, error } = await getApiTokensClient().listApiTokensWithEntitlements(["scim"]);
  if (error || !data?.apiTokens?.length) return false;
  return true;
};
